// Shared building blocks for the metadata every route hands to TanStack
// Router's `head` option (rendered by <HeadContent/> in routes.tsx).
//
// Routes own their metadata rather than components: `head` runs as part of
// navigation, so the title is correct before the page paints. Components can
// only set it from an effect, which is what react-helmet used to do and why
// the analytics tracker had to wait for the title to settle.

import type React from 'react'

// The apex host, not www: www 301s to it, so a canonical or og:url on the www
// form points every crawler and every share at a URL that redirects — which
// splits a post's share count across two addresses.
export const SITE_URL = 'https://thai2english.com'
const SITE_NAME = 'Thai2English'

export type MetaTag = React.JSX.IntrinsicElements['meta']
export type LinkTag = React.JSX.IntrinsicElements['link']
export type ScriptTag = React.JSX.IntrinsicElements['script']

/**
 * A share image. Facebook and X both need the dimensions up front: without
 * them the first scrape has to fetch the file before it can lay the card out,
 * so the first person to share a link usually gets a card with no image on it.
 */
export type OgImage = {
   /** Root-relative or absolute; `pageMeta` emits it absolute either way. */
   url: string
   width: number
   height: number
   alt?: string
}

/**
 * The fallback share image for pages with nothing better. Square, so it stays
 * a `summary` card rather than a stretched banner — see `twitterCard` below.
 */
export const DEFAULT_OG_IMAGE: OgImage = {
   url: '/img/logo/t2e-logo-500.png',
   width: 512,
   height: 512,
   alt: 'Thai2English',
}

/** og:image has to be absolute — a crawler won't resolve a site-relative one. */
export const absoluteUrl = (path: string): string =>
   /^https?:\/\//.test(path) ? path : `${SITE_URL}${path}`

// A wide image earns the big X card; anything squarer would be letterboxed
// into it, so those stay on the small thumbnail layout.
const twitterCard = (image: OgImage): string =>
   image.width >= 600 && image.width / image.height >= 1.5 ? 'summary_large_image' : 'summary'

type PageMetaOptions = {
   title: string
   description: string
   /** Absolute canonical URL. The og:url tag is emitted from it. */
   url: string
   ogType?: 'website' | 'article'
   image?: OgImage
   /** Only meaningful with `ogType: 'article'`; `publishedTime` must be ISO 8601. */
   article?: { publishedTime?: string; author?: string }
}

/**
 * The title/description/Open Graph set every public page shares. Callers add
 * the canonical link separately (see `canonicalLink`) because a link tag and a
 * meta tag go into different buckets of the `head` return value.
 *
 * `twitter:card` is emitted here rather than left to the site-wide default in
 * __root.tsx so a page carrying a wide card gets the large layout: a name/meta
 * tag from a route replaces the root's entry of the same name.
 */
export const pageMeta = ({
   title,
   description,
   url,
   ogType = 'website',
   image = DEFAULT_OG_IMAGE,
   article,
}: PageMetaOptions): MetaTag[] => [
   { title },
   { name: 'description', content: description },
   { property: 'og:site_name', content: SITE_NAME },
   { property: 'og:locale', content: 'en_US' },
   { property: 'og:url', content: url },
   { property: 'og:type', content: ogType },
   { property: 'og:title', content: title },
   { property: 'og:description', content: description },
   { property: 'og:image', content: absoluteUrl(image.url) },
   { property: 'og:image:width', content: String(image.width) },
   { property: 'og:image:height', content: String(image.height) },
   ...(image.alt ? [{ property: 'og:image:alt', content: image.alt }] : []),
   ...(ogType === 'article' && article?.publishedTime
      ? [{ property: 'article:published_time', content: article.publishedTime }]
      : []),
   ...(ogType === 'article' && article?.author
      ? [{ property: 'article:author', content: article.author }]
      : []),
   { name: 'twitter:card', content: twitterCard(image) },
]

export const canonicalLink = (url: string): LinkTag => ({ rel: 'canonical', href: url })

/**
 * Facebook's recommended feed size, and what `npm run generate:og-cards`
 * renders. Kept here so the route's meta and the generator can't disagree.
 */
export const OG_CARD_SIZE = { width: 1200, height: 630 } as const

/**
 * Where a post's generated share card lives. The slug is the only input, so
 * the route can name the file without the generator having to record it —
 * `blog-og-cards.test.ts` fails if one hasn't been generated.
 */
export const ogCardPath = (slug: string): string => `/img/blog/og/${slug}.png`

const MONTHS = [
   'january',
   'february',
   'march',
   'april',
   'may',
   'june',
   'july',
   'august',
   'september',
   'october',
   'november',
   'december',
]

/**
 * The human date a post's frontmatter carries ("July 21, 2026") as the ISO
 * 8601 form `article:published_time` wants. Parsed by hand rather than with
 * `new Date()`, which would read the string in the renderer's timezone and
 * could land the post a day earlier on a server running behind UTC.
 *
 * Returns undefined for anything it doesn't recognise, so an odd date just
 * omits the tag instead of emitting a broken one.
 */
export const isoDate = (date: string): string | undefined => {
   const match = /^([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})$/.exec(date.trim())
   if (!match) return undefined
   const month = MONTHS.indexOf(match[1].toLowerCase())
   if (month < 0) return undefined
   return `${match[3]}-${String(month + 1).padStart(2, '0')}-${match[2].padStart(2, '0')}`
}

/** The `image` a post hands to `pageMeta`. */
export const ogCard = (slug: string, alt: string): OgImage => ({
   url: ogCardPath(slug),
   ...OG_CARD_SIZE,
   alt,
})

/**
 * A dictionary entry's meta description: the headword, its transliteration and
 * as many of its meanings as fit, so each of the ~193,000 entry pages carries
 * a snippet of its own rather than one shared template that search engines
 * treat as near-duplicate boilerplate.
 *
 * Meanings are added whole until the description passes ~155 characters —
 * roughly what Google displays — but the first one is always kept, so a long
 * meaning is truncated by the search engine rather than by us.
 */
export const dictionaryDescription = (
   word: string,
   translit: string | undefined,
   meanings: string[]
): string => {
   const lead = translit ? `${word} (${translit})` : word
   const tail = 'Thai-English dictionary entry with example sentences, audio and usage notes.'
   // Some meanings carry inline markup (`ธาย <i> (transliteration) </i>`),
   // zero-width joiners, or their own ` ; `-separated alternatives; a meta tag
   // is plain text, so drop the tags and tidy the whitespace and separators.
   const senses = meanings
      .map((meaning) =>
         meaning
            .replace(/<[^>]*>/g, ' ')
            .replace(/[\u200B\u200C\u200D\uFEFF]/g, '')
            .replace(/\s+/g, ' ')
            .replace(/\s*;\s*/g, '; ')
            .trim()
      )
      .filter(Boolean)

   let joined = ''
   for (const sense of senses) {
      const next = joined ? `${joined}; ${sense}` : sense
      if (joined && `${lead} — ${next}. ${tail}`.length > 155) break
      joined = next
   }

   return joined ? `${lead} — ${joined}. ${tail}` : `${lead} — ${tail}`
}

/**
 * The canonical URL of a dictionary entry. Thai headwords are percent-encoded,
 * so the tag holds the same ASCII form a crawler will have fetched — a raw Thai
 * canonical and the URL it was served from would look like different pages.
 */
export const dictionaryUrl = (language: 'thai' | 'english', word: string): string =>
   `${SITE_URL}/${language}/${encodeURIComponent(word)}`

/**
 * The transliteration tool's two language variants. Each is a page of its own
 * with its own canonical; `translitAlternates` is emitted identically from
 * both routes because hreflang only counts when every page in the set lists
 * the whole set. The Thai URL is percent-encoded for the same reason
 * `dictionaryUrl`'s is.
 */
export const TRANSLIT_URL_EN = `${SITE_URL}/transliteration`
export const TRANSLIT_URL_TH = `${SITE_URL}/${encodeURIComponent('ภาษาคาราโอเกะ')}`

export const translitAlternates: LinkTag[] = [
   { rel: 'alternate', href: TRANSLIT_URL_EN, hrefLang: 'en' },
   { rel: 'alternate', href: TRANSLIT_URL_TH, hrefLang: 'th' },
   // The variant offered to searchers in any other language.
   { rel: 'alternate', href: TRANSLIT_URL_EN, hrefLang: 'x-default' },
]

/** The site as schema.org publisher, shared by every blob below. */
const publisher = {
   '@type': 'Organization',
   name: SITE_NAME,
   url: SITE_URL,
   logo: absoluteUrl(DEFAULT_OG_IMAGE.url),
}

/**
 * The WebSite blob. Homepage only: WebSite describes the site as a whole, and
 * repeating it on all 193,000 entry pages just hands a crawler that many
 * conflicting copies to reconcile. Entries carry `definedTermSchema`, posts
 * `articleSchema`.
 */
export const websiteSchema = ({ name, description }: { name: string; description: string }) => ({
   '@context': 'https://schema.org',
   '@type': 'WebSite',
   url: `${SITE_URL}/`,
   name,
   description,
   publisher,
})

/**
 * A dictionary entry as a DefinedTerm — schema.org's type for a word defined
 * in a named glossary, which is exactly what an entry page is.
 */
export const definedTermSchema = ({
   url,
   word,
   description,
}: {
   url: string
   word: string
   description: string
}) => ({
   '@context': 'https://schema.org',
   '@type': 'DefinedTerm',
   '@id': url,
   url,
   name: word,
   description,
   inDefinedTermSet: {
      '@type': 'DefinedTermSet',
      name: `${SITE_NAME} Thai-English Dictionary`,
      url: SITE_URL,
   },
})

/** A blog or learn post as a schema.org Article. */
export const articleSchema = ({
   url,
   headline,
   description,
   datePublished,
   author,
   image,
}: {
   url: string
   headline: string
   description: string
   /** ISO 8601 — pass frontmatter dates through `isoDate`. */
   datePublished?: string
   author?: string
   /** Root-relative or absolute; emitted absolute. */
   image?: string
}) => ({
   '@context': 'https://schema.org',
   '@type': 'Article',
   url,
   headline,
   description,
   ...(datePublished ? { datePublished } : {}),
   ...(author ? { author: { '@type': 'Person', name: author } } : {}),
   ...(image ? { image: absoluteUrl(image) } : {}),
   publisher,
})

/**
 * The /blog landing page as a schema.org Blog. Each post is listed as a
 * BlogPosting stub — headline, URL and date only, since the post's own page
 * carries the full `articleSchema` and a crawler should take the detail from
 * there rather than from a listing's copy of it.
 */
export const blogSchema = ({
   url,
   name,
   description,
   posts,
}: {
   url: string
   name: string
   description: string
   posts: { url: string; headline: string; datePublished?: string; author?: string }[]
}) => ({
   '@context': 'https://schema.org',
   '@type': 'Blog',
   '@id': url,
   url,
   name,
   description,
   publisher,
   blogPost: posts.map(({ url: postUrl, headline, datePublished, author }) => ({
      '@type': 'BlogPosting',
      url: postUrl,
      headline,
      ...(datePublished ? { datePublished } : {}),
      ...(author ? { author: { '@type': 'Person', name: author } } : {}),
   })),
})

/**
 * A schema.org blob as the `<script type="application/ld+json">` tag `head`
 * expects.
 *
 * `<` is escaped to its \u form — still valid JSON, but it means a headword
 * containing something like `</script>` can't close the tag early. The
 * hand-built template strings this replaced had no such protection.
 */
export const ldJsonScript = (schema: unknown): ScriptTag => ({
   type: 'application/ld+json',
   children: JSON.stringify(schema).replace(/</g, '\\u003c'),
})
